import { FormConfig } from 'common.types'
import {
  COUNTRIES,
  PA_COUNTIES,
  UNIT_TYPES,
  US_STATES__KEYS,
} from '../form.constants'
import {
  MailInBallotAboveFieldsContent,
  MailInBallotBelowFieldsContent,
  MailInBallotHeaderContent,
  MailInBallotSuccessScreen,
} from '../mail-in-ballot/components'
import { getMunicipalitiesByCounty } from 'utilities/utilities'
import validateMailInBallotEvent from '../mail-in-ballot/helpers/mail-in-ballot.event-validator'

export const mailInBallotConfig: FormConfig = {
  captchaStep: 1,
  requireEitherEmailOrPhone: true,
  singlePage: true,
  fields: [
    {
      name: 'disclosureThree',
      type: 'accordion',
      step: 1,
    },
    {
      name: 'inputsMustMatchInfo',
      type: 'html',
      step: 1,
    },
    {
      name: 'firstName',
      maxLength: 30,
      type: 'text',
      required: true,
      step: 1,
    },
    {
      name: 'middleNameInitial',
      type: 'text',
      maxLength: 30,
      required: false,
      step: 1,
    },
    {
      name: 'lastName',
      maxLength: 30,
      type: 'text',
      required: true,
      step: 1,
    },
    {
      name: 'suffix',
      type: 'select',
      step: 1,
      selectOptions: ['I', 'II', 'III', 'IV', 'V', 'VI', 'VII', 'JR', 'SR'],
    },
    {
      name: 'birthDate',
      type: 'date',
      required: true,
      validate: 'date',
      step: 1,
      maxDefaultDate: new Date('11/04/2007'),
      minDate: new Date('01/01/1900'),
    },
    {
      name: 'county',
      type: 'select',
      selectOptions: PA_COUNTIES,
      selectLabelsFromOptions: true,
      required: true,
      step: 1,
    },
    {
      name: 'zip',
      type: 'text',
      required: true,
      validate: 'zip',
      step: 1,
    },
    {
      name: 'email',
      maxLength: 50,
      type: 'text',
      step: 1,
      validate: 'email',
    },
    {
      name: 'phone',
      type: 'phone',
      step: 1,
      customErrorMessage: true,
      validate: 'phone',
    },
    {
      name: 'optInSms',
      type: 'checkbox',
      step: 1,
    },
    {
      name: 'dataConsent',
      type: 'checkbox',
      required: true,
      step: 1,
    },
    {
      name: 'address',
      maxLength: 50,
      type: 'text',
      required: true,
      step: 2,
    },
    {
      name: 'addressTwo',
      maxLength: 25,
      type: 'text',
      step: 2,
    },
    {
      name: 'unitType',
      type: 'select',
      selectOptions: UNIT_TYPES,
      step: 2,
    },
    {
      name: 'unitNumber',
      type: 'text',
      maxLength: 8,
      step: 2,
    },
    {
      name: 'city',
      maxLength: 18,
      type: 'text',
      required: true,
      step: 2,
    },
    {
      name: 'municipality',
      type: 'text',
      maxLength: 25,
      step: 2,
    },
    {
      name: 'livedSince',
      type: 'date',
      validate: 'date',
      minDate: new Date('01/01/1900'),
      maxDefaultDate: new Date(),
      dateOnlyYear: true,
      required: true,
      customErrorMessage: true,
      step: 2,
    },
    {
      name: 'hasAltMailingAddress',
      type: 'checkbox',
      step: 2,
    },
    {
      name: 'altAddress',
      maxLength: 40,
      type: 'text',
      required: true,
      step: 2,
      conditionalOn: 'hasAltMailingAddress',
    },
    {
      name: 'altAddressTwo',
      maxLength: 25,
      type: 'text',
      step: 2,
      conditionalOn: 'hasAltMailingAddress',
    },
    {
      name: 'altUnitType',
      type: 'select',
      selectOptions: UNIT_TYPES,
      conditionalOn: 'hasAltMailingAddress',
      step: 2,
    },
    {
      name: 'altUnitNumber',
      type: 'text',
      maxLength: 8,
      conditionalOn: 'hasAltMailingAddress',
      step: 2,
    },
    {
      name: 'altCity',
      maxLength: 20,
      type: 'text',
      required: true,
      step: 2,
      conditionalOn: 'hasAltMailingAddress',
    },
    {
      name: 'altState',
      type: 'select',
      selectOptions: US_STATES__KEYS,
      required: true,
      step: 2,
      conditionalOn: 'hasAltMailingAddress',
      selectLabelsFromOptions: true,
      overrideSelectWithTextControllerField: 'altOutsideUs',
      maxLength: 20,
    },
    {
      name: 'altZip',
      type: 'text',
      required: true,
      step: 2,
      conditionalOn: 'hasAltMailingAddress',
      zipInternationalField: 'altOutsideUs',
      postalCodeInternationalControllerField: 'altOutsideUs',
      validate: 'zip',
    },
    {
      name: 'altOutsideUs',
      type: 'checkbox',
      step: 2,
      conditionalOn: 'hasAltMailingAddress',
    },
    {
      name: 'altCountry',
      type: 'select',
      required: true,
      selectOptions: COUNTRIES,
      step: 2,
      conditionalOn: 'altOutsideUs',
      selectLabelsFromOptions: true,
    },
    {
      name: 'altAddressIdentity',
      type: 'text',
      maxLength: 30,
      conditionalOn: 'hasAltMailingAddress',
      step: 2,
    },
    {
      name: 'idCardNumber',
      type: 'text',
      required: true,
      step: 3,
      validate: 'pennDotId',
    },
    {
      name: 'futureDeclaration',
      boldedCheckboxLabel: true,
      type: 'checkbox',
      step: 3,
    },
    {
      name: 'agreementTerms',
      type: 'html',
      step: 3,
    },
    {
      name: 'agreement',
      type: 'checkbox',
      customRequiredErrorMessage: true,
      boldedCheckboxLabel: true,
      required: true,
      step: 3,
    },
  ],
  successScreen: MailInBallotSuccessScreen,
  validateEventResponse: validateMailInBallotEvent,
  validateEventResponseType: 'submit',
  headerContent: MailInBallotHeaderContent,
  aboveFieldsContent: MailInBallotAboveFieldsContent,
  belowFieldsContent: MailInBallotBelowFieldsContent,
}
